/**
 * opora-account.js — Shared client-side auth + account utilities
 *
 * Created: Wave 0 (2026-06-18)
 * Rule: "Free to use today — no subscription." requirePaidAuth is NOT present here.
 *
 * Exports (window.OporaAccount):
 *   getClerkToken()       → Promise<string|null>  — Clerk JWT or null for anon
 *   getAuthHeaders()      → Promise<object>        — { Authorization: 'Bearer …' } or {}
 *   isSignedIn()          → boolean                — sync check (does Clerk session exist)
 *   onAuthChange(fn)      → void                   — subscribe to sign-in/out events
 *   getSignInUrl(return)  → string                 — dashboard auth URL (no per-page redirect query)
 *   getSignUpUrl(return)  → string                 — dashboard sign-up URL
 *   promptSignIn(intent)  → void                   — stash return path, then navigate to dashboard auth
 *
 * No requirePaidAuth. No plan checks. Tools are free to use.
 * Auth is used ONLY for: saving artifacts, autosave, profile persistence.
 */

(function (global) {
  'use strict';

  // ─────────────────────────────────────────
  // Constants
  // ─────────────────────────────────────────
  const SIGNIN_PATH  = '/account/login'; // legacy path; prefer getSignInUrl() / promptSignIn()
  const SIGNUP_PATH  = '/account/register';

  // ─────────────────────────────────────────
  // getClerkToken — async, real JWT
  // ─────────────────────────────────────────
  /**
   * Returns the Clerk JWT for the current session, or null.
   * Wave 0 fix: previously returned null for authenticated users due to
   * an inverted condition. Now correctly awaits session.getToken().
   */
  async function getClerkToken() {
    try {
      if (global.Clerk && global.Clerk.session) {
        const token = await global.Clerk.session.getToken();
        return token ?? null;
      }
    } catch {
      // Clerk not loaded, session expired, or CORS issue — fall through
    }
    return null;
  }

  // ─────────────────────────────────────────
  // getAuthHeaders — async, returns fetch-ready headers
  // ─────────────────────────────────────────
  async function getAuthHeaders() {
    const token = await getClerkToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // ─────────────────────────────────────────
  // isSignedIn — sync (check if session object exists)
  // ─────────────────────────────────────────
  function isSignedIn() {
    try {
      return !!(global.Clerk && global.Clerk.session);
    } catch {
      return false;
    }
  }

  // ─────────────────────────────────────────
  // onAuthChange — subscribe to sign-in/out events
  // ─────────────────────────────────────────
  const _authListeners = [];
  function onAuthChange(fn) {
    if (typeof fn !== 'function') return;
    _authListeners.push(fn);
  }

  // Fire listeners when Clerk dispatches auth events
  function _fireAuthChange(event) {
    const signed = isSignedIn();
    _authListeners.forEach(fn => {
      try { fn({ signedIn: signed, event }); } catch {}
    });
  }

  // Hook into Clerk events if Clerk is loaded
  function _attachClerkListeners() {
    if (!global.Clerk) return;
    try {
      global.Clerk.addListener(({ session }) => {
        _fireAuthChange(session ? 'signed_in' : 'signed_out');
      });
    } catch {
      // Clerk version may not support addListener
    }
  }

  // Try to attach now; retry once Clerk is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      _attachClerkListeners();
      // Retry after Clerk script loads
      setTimeout(_attachClerkListeners, 1500);
    });
  } else {
    _attachClerkListeners();
    setTimeout(_attachClerkListeners, 1500);
  }

  // ─────────────────────────────────────────
  // getSignInUrl / getSignUpUrl
  // ─────────────────────────────────────────
  function dashBase() {
    return ((global.OporaEdge && global.OporaEdge.DASHBOARD_URL) || 'https://dashboard.oporasupply.com').replace(/\/$/, '');
  }

  function promptSignIn(intent) {
    intent = intent || 'sign-in';
    if (global.OporaEdge && typeof global.OporaEdge.stashAuthReturn === 'function') {
      global.OporaEdge.stashAuthReturn();
    } else {
      try { sessionStorage.setItem('_opora_auth_return', global.location.href); } catch {}
    }
    if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function' && !global.OporaAuth.__oporaStub) {
      global.OporaAuth.openModal({
        intent: intent === 'sign-up' ? 'sign-up' : 'sign-in',
        inPlace: true
      });
      return;
    }
    var url = (global.OporaEdge && typeof global.OporaEdge.dashboardAuthUrl === 'function')
      ? global.OporaEdge.dashboardAuthUrl(intent)
      : (dashBase() + (intent === 'sign-up' ? '/?intent=sign-up' : '/'));
    global.location.href = url;
  }

  function getSignInUrl(returnPath) {
    // Return path is stashed in sessionStorage via promptSignIn / data-opora-signin-cta —
    // never encode it into a crawlable query string on www.
    void returnPath;
    return dashBase() + '/';
  }

  function getSignUpUrl(returnPath) {
    void returnPath;
    return dashBase() + '/?intent=sign-up';
  }

  // ─────────────────────────────────────────
  // Sign-in nudge helper (shared across all tools)
  // Renders a non-blocking banner: "Sign in to save"
  // Preserves the result the user just produced (result JSON in sessionStorage).
  // After sign-in, the tool page reloads and can restore from sessionStorage.
  // ─────────────────────────────────────────
  function showSignInToSaveNudge(opts) {
    const {
      toolSlug = '',
      resultKey = null,   // sessionStorage key holding the result to preserve
      containerId = null, // DOM id to insert banner before/after
      position = 'before' // 'before' | 'after' relative to container
    } = opts || {};

    const existingId = `opora-signin-nudge-${toolSlug}`;
    if (document.getElementById(existingId)) return;

    // Preserve return URL for post-auth redirect (not in the href).
    if (global.OporaEdge && typeof global.OporaEdge.stashAuthReturn === 'function') {
      global.OporaEdge.stashAuthReturn();
    } else {
      try { sessionStorage.setItem('_opora_auth_return', global.location.href); } catch {}
    }

    const signInUrl = getSignInUrl();

    const banner = document.createElement('div');
    banner.id = existingId;
    banner.className = 'opora-signin-nudge';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML = `
      <div class="opora-signin-nudge__inner">
        <svg class="opora-signin-nudge__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/>
        </svg>
        <span class="opora-signin-nudge__text">Sign in to save this to your library — your result is ready.</span>
        <a href="${signInUrl}" class="opora-signin-nudge__cta" rel="nofollow" data-opora-signin-cta>Sign in</a>
        <button class="opora-signin-nudge__dismiss" aria-label="Dismiss sign-in prompt" type="button">
          <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/></svg>
        </button>
      </div>
    `;

    banner.querySelector('.opora-signin-nudge__dismiss')?.addEventListener('click', () => {
      banner.classList.add('is-dismissing');
      setTimeout(() => banner.remove(), 200);
    });

    const cta = banner.querySelector('.opora-signin-nudge__cta');
    if (cta) {
      cta.addEventListener('click', (e) => {
        e.preventDefault();
        if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
          global.OporaAuth.openModal({
            intent: 'sign-up',
            inPlace: true,
            title: 'Save this result',
            tagline: 'Create a free account to edit, duplicate, and access this result across devices.'
          });
        } else {
          promptSignIn('sign-in');
        }
      });
    }

    // Insert relative to container or at top of body
    const container = containerId ? document.getElementById(containerId) : null;
    if (container) {
      if (position === 'before') {
        container.insertAdjacentElement('beforebegin', banner);
      } else {
        container.insertAdjacentElement('afterend', banner);
      }
    } else {
      // Fallback: prepend to main content area
      const main = document.querySelector('main') || document.body;
      main.prepend(banner);
    }
  }

  // ─────────────────────────────────────────
  // Export
  // ─────────────────────────────────────────
  global.OporaAccount = {
    getClerkToken,
    getAuthHeaders,
    isSignedIn,
    onAuthChange,
    getSignInUrl,
    getSignUpUrl,
    showSignInToSaveNudge,
    promptSignIn,
    dashBase,
  };

  // Delegated handler for SSR/theme CTAs marked data-opora-signin-cta —
  // stashes return URL, then uses dashboard auth (never unique /account/login?redirect=…).
  document.addEventListener('click', function (e) {
    const a = e.target && e.target.closest && e.target.closest('[data-opora-signin-cta]');
    if (!a) return;
    e.preventDefault();
    const intent = a.getAttribute('data-opora-intent') || 'sign-in';
    if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
      if (global.OporaEdge && typeof global.OporaEdge.stashAuthReturn === 'function') {
        global.OporaEdge.stashAuthReturn();
      } else {
        try { sessionStorage.setItem('_opora_auth_return', global.location.href); } catch {}
      }
      global.OporaAuth.openModal({
        intent: intent === 'sign-up' ? 'sign-up' : 'sign-in',
        inPlace: true,
        title: 'Save this result',
        tagline: 'Create a free account to edit, duplicate, and access this result across devices.'
      });
      return;
    }
    promptSignIn(intent);
  }, true);

})(window);